import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { SESSION_DURATION_MINUTES } from "../client";
import { provision } from "../api/provision";
import { useStytchClient } from "../hooks/useSession";
import { takeReturnPath } from "../session";
import { failureReason } from "../types";
import AuthFrame from "./AuthFrame";

import type { AuthErrorCode } from "../types";

/* Where Stytch lands the browser after OAuth and magic links alike.

   The sequence is CONTRACT.md's, in order and without shortcuts:
     authenticate → POST /auth/provision → session.authenticate()
   The re-mint matters: provisioning is what puts `{user_id, org_id}` into the
   user's trusted metadata, and the JWT minted a moment earlier does not carry
   them. Skipping it means the first API call after signup is unauthorised.

   Any failure hands the reason to /login rather than stranding the user here. */
export default function AuthCallbackPage() {
  const stytch = useStytchClient();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // StrictMode mounts effects twice in dev, and these tokens are single-use.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = params.get("token");
    const tokenType = params.get("stytch_token_type");
    /* The reason rides along to /login so the user reads what actually failed
       rather than our one-size-fits-all sentence — this page is about to
       unmount, so there is nowhere else to put it. */
    const fail = (code: AuthErrorCode, reason?: string | null) =>
      navigate(`/login?error=${code}${reason ? `&reason=${encodeURIComponent(reason)}` : ""}`, {
        replace: true,
      });

    (async () => {
      if (!token) return fail("missing_token");

      const options = { session_duration_minutes: SESSION_DURATION_MINUTES };
      try {
        if (tokenType === "oauth") await stytch.oauth.authenticate(token, options);
        else if (tokenType === "magic_links") await stytch.magicLinks.authenticate(token, options);
        else return fail("unsupported_token");
      } catch (e) {
        console.error(e);
        return fail("authenticate_failed", failureReason(e));
      }

      try {
        await provision();
        await stytch.session.authenticate({ session_duration_minutes: SESSION_DURATION_MINUTES });
      } catch (e) {
        console.error(e);
        return fail("provision_failed", failureReason(e));
      }

      navigate(takeReturnPath(), { replace: true });
    })();
  }, [params, navigate, stytch]);

  return (
    <AuthFrame>
      <div className="fade-in flex flex-col items-center gap-2">
        <span className="shimmer-text text-[13px] font-medium tracking-[-0.005em]">
          Signing you in…
        </span>
      </div>
    </AuthFrame>
  );
}
